import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

import Login from './pages/Login';
import Register from './pages/Register';
import Membership from './pages/Membership';
import Dashboard from './pages/Dashboard';
import Academy from './pages/Academy';
import Lesson from './pages/Lesson';
import Quiz from './pages/Quiz';
import Certificate from './pages/Certificate';
import Calculator from './pages/Calculator';
import Practice from './pages/Practice';
import Referral from './pages/Referral';
import WalletPage from './pages/Wallet';
import AITutor from './pages/AITutor';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPayments from './pages/admin/AdminPayments';
import AdminMembers from './pages/admin/AdminMembers';
import AdminMemberDetail from './pages/admin/AdminMemberDetail';
import AdminMemberships from './pages/admin/AdminMemberships';
import AdminCommissions from './pages/admin/AdminCommissions';
import AdminFinance from './pages/admin/AdminFinance';
import AdminWithdrawals from './pages/admin/AdminWithdrawals';
import AdminModeration from './pages/admin/AdminModeration';
import AdminSettings from './pages/admin/AdminSettings';
import AdminJobs from './pages/admin/AdminJobs';
import AdminReports from './pages/admin/AdminReports';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSystem from './pages/admin/AdminSystem';

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="h-10 w-10 rounded-full border-4 border-[hsl(var(--brand-gold))] border-t-transparent animate-spin" />
  </div>
);

const Protected = ({ children, admin }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/membership" element={<Protected><Membership /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/academy" element={<Protected><Academy /></Protected>} />
            <Route path="/academy/lesson/:lessonId" element={<Protected><Lesson /></Protected>} />
            <Route path="/academy/quiz/:quizId" element={<Protected><Quiz /></Protected>} />
            <Route path="/certificate" element={<Protected><Certificate /></Protected>} />
            <Route path="/calculator" element={<Protected><Calculator /></Protected>} />
            <Route path="/practice" element={<Protected><Practice /></Protected>} />
            <Route path="/referral" element={<Protected><Referral /></Protected>} />
            <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
            <Route path="/tutor" element={<Protected><AITutor /></Protected>} />
            <Route path="/admin" element={<Protected admin><AdminDashboard /></Protected>} />
            <Route path="/admin/payments" element={<Protected admin><AdminPayments /></Protected>} />
            <Route path="/admin/members" element={<Protected admin><AdminMembers /></Protected>} />
            <Route path="/admin/members/:userId" element={<Protected admin><AdminMemberDetail /></Protected>} />
            <Route path="/admin/memberships" element={<Protected admin><AdminMemberships /></Protected>} />
            <Route path="/admin/commissions" element={<Protected admin><AdminCommissions /></Protected>} />
            <Route path="/admin/finance" element={<Protected admin><AdminFinance /></Protected>} />
            <Route path="/admin/withdrawals" element={<Protected admin><AdminWithdrawals /></Protected>} />
            <Route path="/admin/jobs" element={<Protected admin><AdminJobs /></Protected>} />
            <Route path="/admin/moderation" element={<Protected admin><AdminModeration /></Protected>} />
            <Route path="/admin/reports" element={<Protected admin><AdminReports /></Protected>} />
            <Route path="/admin/settings" element={<Protected admin><AdminSettings /></Protected>} />
            <Route path="/admin/users" element={<Protected admin><AdminUsers /></Protected>} />
            <Route path="/admin/system" element={<Protected admin><AdminSystem /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
